<template>
	<KkSelectLayout
		ref="selectLayoutRef"
		class="kk-select-with-menu"
		@open:menu="onOpenMenu"
		@close:menu="onCloseMenu"
	>
		<template #button>
			<KkSelect
				ref="selectInputRef"
				v-bind="$attrs"
				:is-filterable="isFilterable"
				:is-disabled="isDisabled"
				:is-expanded="isExpanded"
				:value="showValue"
				:query="query"
				:leading-image="leadingImage"
				:placeholder="placeholder"
				:trailing-icon="trailingIcon"
				:has-error="hasError"
				:has-trailing-transition="hasTrailingTransition"
				@compositionstart="onCompositionHandler"
				@compositionupdate="onCompositionHandler"
				@compositionend="onCompositionHandler"
				@input="onInputHandler"
				@focus="onFocusHandler"
				@click.native="toggleMenu"
				@update:query="updateQuery"
			>
				<slot
					name="select-content"
					v-bind="selectedOption"
				></slot>
			</KkSelect>
		</template>
		<template #menu>
			<KkDropdown
				ref="selectDropdownRef"
				:is-auto-width="!isFillWithSelect"
				:style="dropdownStyles"
			>
				<div
					v-if="isLoading"
					class="kk-select-with-menu__loading"
				>
					<LoadingIcon />
				</div>
				<template v-else-if="$slots['default']">
					<div v-show="!isEmptyState">
						<slot />
					</div>
					<div
						v-show="isEmptyState"
						class="kk-select-with-menu__empty-text"
					>
						{{ emptyText }}
					</div>
				</template>
				<div
					v-else
					class="kk-select-with-menu__empty-text"
				>
					{{ emptyText }}
				</div>
			</KkDropdown>
		</template>
	</KkSelectLayout>
</template>

<script>
import {
	computed,
	provide,
	reactive,
	ref,
	nextTick,
	watch,
} from 'vue';
import { omit, isEqual, isFunction } from 'lodash-es';
import KkSelectLayout from '../../KkSelectLayout';
import KkSelect, { KkSelectProps } from '../../KkSelect';
import KkDropdown from '../../KkDropdown';
import LoadingIcon from './LoadingIcon.vue';
import { useDropdownStyle } from './useDropdownStyle';

export default {
	name: 'KkSelectWithMenu',
	components: {
		KkSelectLayout,
		KkSelect,
		KkDropdown,
		LoadingIcon,
	},
	props: {
		...omit(KkSelectProps, ['isExpanded']),
		isFillWithSelect: {
			type: Boolean,
			default: true,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		emptyText: {
			type: String,
			default: 'No result'
		},
		filterMethod: {
			type: Function,
			default: null,
		},
	},
	setup(props, { emit }) {
		const isExpanded = ref(false);
		const isOnComposition = ref(false);
		const optionList = ref([]);
		const selectedOption = ref(null);
		const selectDropdownRef = ref(null);
		const selectInputRef = ref(null);
		const selectLayoutRef = ref(null);
		const query = ref('');

		const { dropdownStyles, keepDropdownWidth } = useDropdownStyle({
			isFillWithSelect: props.isFillWithSelect,
			dropdownRef: selectDropdownRef,
			selectRef: selectInputRef,
		});
		const value = computed(() => props.value);
		const isEmptyState = computed(() => {
			return optionList.value.length > 0 && optionList.value.filter(ins => ins.isVisible).length === 0;
		});
		const showValue = computed(() => {
			if (props.value !== undefined && props.value !== null) {
				return selectedOption.value?.label || '';
			}
			return '';
		})
		function toggleMenu() {
			if (props.isDisabled) {
				return;
			}
			selectLayoutRef.value.toggleMenu();
			if (selectLayoutRef.value.isMenuOpen) {
				selectInputRef.value.setSelectFocus(true);
				nextTick(() => {
					keepDropdownWidth();
					selectDropdownRef.value.centerElement(selectedOption.value?.el);
				})
				return;
			}
			selectInputRef.value.setSelectFocus(false);
		}
		function onOpenMenu() {
			isExpanded.value = true;
		}
		function onCloseMenu() {
			handleQueryChange('');
			isExpanded.value = false;
			selectInputRef.value.setSelectFocus(false);
			emit('blur');
		}
		function updateQuery(val) {
			if (isOnComposition.value) {
				return;
			}
			handleQueryChange(val);
		}
		function addOption(vueIns) {
			optionList.value.push(vueIns);
		}
		function setSelected({ value, label, el }) {
			selectedOption.value = {
				value,
				label,
				el,
			};
			if (!isEqual(props.value, value)) {
				emit('input', value);
				emit('change', value);
			}
		}
		function onOptionSelect({ value, label, el }) {
			setSelected({ value, label, el })
			if (selectLayoutRef.value) {
				selectLayoutRef.value.closeMenu();
			}
		}
		function onInputHandler($event) {
			emit('input', $event);
		}
		function onFocusHandler($event) {
			emit('focus', $event);
		}
		function handleQueryChange(text) {
			query.value = text;
			if (isFunction(props.filterMethod)) {
				props.filterMethod(text);
			}
		}
		const onCompositionHandler = (event) => {
			if (event.type === 'compositionend') {
				isOnComposition.value = false
				const text = event.target.value
				handleQueryChange(text);
				return;
			}
			isOnComposition.value = true;
		}
		watch(
			() => props.value,
			(val) => {
				if (!val) {
					selectedOption.value = null;
				}
			},
		)
		provide('KkSelectWithMenu', reactive({
			onOptionSelect,	// KkSelectOption 被選取時執行
			setSelected,	// KkSelectOption 狀態 isSelected 改變時執行
			addOption,
			value,
			// 外部傳入客製篩選功能時，不讓子元件自己判斷
			query: props.filterMethod ? '' : query,
		}));

		return {
			toggleMenu,
			onOpenMenu,
			onCloseMenu,
			isExpanded,
			selectInputRef,
			selectDropdownRef,
			selectLayoutRef,
			dropdownStyles,
			selectedOption,
			query,
			showValue,
			updateQuery,
			isEmptyState,
			onCompositionHandler,
			onInputHandler,
			onFocusHandler,
		}
	}
}
</script>
